<template>
  <UkaskoLoader :is-loading="!isActivityTypesReady">
    <FormContainer class="register__personal-data-form__form-container">
      <div class="personal-data-form__text-block">
        <h2 class="personal-data-form__title">Вітаю в UConnect</h2>
        <p class="personal-data-form__description">Ми надійна CRM-платформа для агентів і партнерських мереж</p>
      </div>
      <StatusProgress
        class="personal-data-form__progress"
        :current-step="getCurrentStep"
      />
      <form class="personal-data__form" @submit.prevent="handleSubmit">
        <LabeledTextInput
          class="personal-data__input--name"
          label="Імʼя"
          placeholder="Введіть імʼя"
          v-model="form.name"
          :error-messages="errors.name ? [errors.name] : []"
          @input="clearError('name')"
        />
        <LabeledTextInput
          class="personal-data__input--last-name"
          label="Прізвище"
          placeholder="Введіть прізвище"
          v-model="form.lastName"
          :error-messages="errors.lastName ? [errors.lastName] : []"
          @input="clearError('lastName')"
        />
        <LabeledTextInput
          class="personal-data__input--patronymic"
          label="По-батькові"
          placeholder="Введіть по-батькові"
          v-model="form.patronymic"
          :error-messages="errors.patronymic ? [errors.patronymic] : []"
          @input="clearError('patronymic')"
        />
        <LabeledSelectInput
          class="personal-data__input--activity"
          label="Вид діяльності"
          placeholder="Оберіть вид діяльності"
          :options="prepareActivities"
          icon-color="#6171E4"
          v-model="form.activity"
          :error-messages="errors.activity ? [errors.activity] : []"
        />
        <div class="personal-data__next-step">
          <OrangeFancyButton
            class="personal-data__next-step--button"
            type="submit"
            :disabled="!canSwitchToNextStep"
          >
            Далі
          </OrangeFancyButton>
          <a
            class="personal-data__prev-step--button"
            @click="switchPreviousStep"
          >
            Назад
          </a>
        </div>
        <div class="personal-data__login">
          <p class="personal-data__login-text">
            У вас вже є аккаунт?
            <SmartLink
              class="personal-data__link--login"
              :route="{ name: 'login' }"
            >
              Увійти
            </SmartLink>
          </p>
        </div>
      </form>
    </FormContainer>
  </UkaskoLoader>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import StatusProgress from '../status-progress';
import { FormContainer } from '@/shared/ui/account';
import { LabeledSelectInput, LabeledTextInput, OrangeFancyButton } from '@/shared/ui/form';
import { SmartLink } from '@/shared/ui/link';
import { UkaskoLoader } from '@/shared/ui/ukasko-loader';

const INSURANCE_AGENT_INDIVIDUAL_ID = 2;

export default {
  data() {
    return {
      form: {
        name: '',
        lastName: '',
        patronymic: '',
        activity: '',
      },
      errors: {
        name: '',
        lastName: '',
        patronymic: '',
        activity: '',
      },
    };
  },
  created() {
    this.form.name = this.getAccountName;
    this.form.lastName = this.getAccountLastName;
    this.form.patronymic = this.getAccountPatronymic;
    this.form.activity = this.getAccountActivity;
  },
  components: {
    SmartLink, OrangeFancyButton, LabeledTextInput, LabeledSelectInput, StatusProgress, FormContainer, UkaskoLoader
  },
  computed: {
    ...mapGetters('registerModule', [
      'getCurrentStep', 'getAccountName', 'getAccountLastName', 'getAccountPatronymic', 'getAccountActivity',
    ]),
    ...mapGetters('activityTypeModule', ['isActivityTypesReady', 'getActivityTypes']),
    prepareActivities() {
      return this.getActivityTypes?.filter((activityType) => activityType.id !== INSURANCE_AGENT_INDIVIDUAL_ID)?.map(activityType => {
        return { value: activityType.id, label: activityType.name };
      });
    },
    canSwitchToNextStep() {
      return !!this.form.name && !!this.form.lastName && !!this.form.patronymic && !!this.form.activity;
    },
  },
  methods: {
    ...mapActions('registerModule', ['switchCurrentStep', 'switchPreviousStep', 'setAccountPersonalData']),
    clearError(field) {
      this.errors[field] = '';
    },
    validateForm() {
      this.errors.name = !this.form.name.trim() ? 'Ім\'я обов\'язкове до заповнення' : null;
      this.errors.lastName = !this.form.lastName.trim() ? 'Прізвище обов\'язкове до заповнення' : null;
      this.errors.patronymic = !this.form.patronymic.trim() ? 'По-батькові обов\'язкове до заповнення' : null;
      this.errors.activity = !this.form.activity ? 'Оберіть вид діяльності' : null;

      return !this.errors.name || !this.errors.lastName || !this.errors.patronymic || !this.errors.activity;
    },
    async handleSubmit() {
      if (this.validateForm()) {
        this.setAccountPersonalData({
          name: this.form.name,
          lastName: this.form.lastName,
          patronymic: this.form.patronymic,
          activity: this.form.activity,
        });
        this.switchCurrentStep();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.register__personal-data-form {
  &__form-container {
    margin: 170px auto 0;
    width: 600px;

    @media (max-width: 630px) {
      margin: 50px 16px 0;
      width: auto;
    }
  }
}

.personal-data-form {
  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 630px) {
      font-size: 24px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #313957;
    text-align: center;

    @media (max-width: 630px) {
      font-size: 14px;
    }
  }

  &__progress {
    margin-bottom: 16px;
  }
}

.personal-data {
  &__input {
    &--name, &--last-name, &--patronymic {
      margin-bottom: 16px;
    }

    &--activity {
      margin-bottom: 32px;
    }
  }

  &__next-step {
    margin-bottom: 32px;
  }

  &__prev-step {
    &--button {
      display: inline-block;
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #313957;
      padding: 16px 24px;
      width: 100%;
      text-align: center;
      cursor: pointer;
    }
  }

  &__login {
    text-align: center;
  }

  &__link {
    &--login {
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #6171E4;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  margin-bottom: 8px;
}

::v-deep .input-text, .input-text, ::v-deep .select, .select {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &--has-error {
    border: 2px solid #FF3B30 !important;
  }
}

::v-deep .select__value {
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  color: #494E53 !important;
}

::v-deep .select__value--placeholder {
  color: #8E9DB7 !important;
}

::v-deep .select__dropdown {
  background-color: #FFFFFF !important;
  border: none !important;
  border-radius: 16px !important;
  padding: 4px !important;
  box-shadow: 0px 24px 30px 0px #00000021 !important;
}

::v-deep .select__option {
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  color: #313437 !important;
  padding: 16px 24px !important;
  border-radius: 16px !important;
}

::v-deep .select__option:hover {
  background-color: #EBF2FF !important;
  border-radius: 16px !important;
}
</style>
